import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';

const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444'];

export const AnalyticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [dailyRides, setDailyRides] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [paymentBreakdown, setPaymentBreakdown] = useState([]);
  const [stats, setStats] = useState({ totalRides: 0, totalRevenue: 0, activeDrivers: 0, totalUsers: 0 });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get all rides
        const ridesSnapshot = await getDocs(collection(db, 'rides'));
        const rides = ridesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Total rides & revenue
        const totalRides = rides.length;
        let totalRevenue = 0;
        rides.forEach(ride => {
          if (ride.paymentMethod === 'card' && ride.finalFareCents) {
            totalRevenue += ride.finalFareCents;
          } else if (ride.estimatedFareCents) {
            totalRevenue += ride.estimatedFareCents;
          }
        });
        totalRevenue = totalRevenue / 100; // convert to ZAR

        // Active drivers (online in last hour)
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
        const driversSnapshot = await getDocs(collection(db, 'driver_states'));
        let activeDrivers = 0;
        driversSnapshot.forEach(doc => {
          const data = doc.data();
          if (data.online === true && data.lastSeen && data.lastSeen.toDate() > oneHourAgo) {
            activeDrivers++;
          }
        });

        // Total users
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const totalUsers = usersSnapshot.size;

        setStats({ totalRides, totalRevenue: totalRevenue.toFixed(2), activeDrivers, totalUsers });

        // Daily rides for last 7 days
        const last7Days = [];
        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          date.setHours(0, 0, 0, 0);
          const nextDay = new Date(date);
          nextDay.setDate(date.getDate() + 1);
          const dayRides = rides.filter(ride => {
            const createdAt = ride.createdAt?.toDate();
            return createdAt && createdAt >= date && createdAt < nextDay;
          });
          last7Days.push({
            name: date.toLocaleDateString('en-US', { weekday: 'short' }),
            rides: dayRides.length,
            revenue: dayRides.reduce((sum, r) => sum + ((r.finalFareCents || r.estimatedFareCents || 0) / 100), 0)
          });
        }
        setDailyRides(last7Days);
        setRevenueData(last7Days);

        // Payment method breakdown
        const cardCount = rides.filter(r => r.paymentMethod === 'card').length;
        const cashCount = rides.filter(r => r.paymentMethod === 'cash').length;
        const walletCount = rides.filter(r => r.paymentMethod === 'wallet').length;
        setPaymentBreakdown([
          { name: 'Card', value: cardCount },
          { name: 'Cash', value: cashCount },
          { name: 'Wallet', value: walletCount },
        ]);

      } catch (err) {
        console.error('Analytics fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div className="flex justify-center p-8"><div className="text-gray-400">Loading analytics...</div></div>;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-gray-800 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-emerald-400">{stats.totalRides}</div>
          <div className="text-gray-400 text-sm">Total Rides</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-emerald-400">R{stats.totalRevenue}</div>
          <div className="text-gray-400 text-sm">Revenue (ZAR)</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-emerald-400">{stats.activeDrivers}</div>
          <div className="text-gray-400 text-sm">Active Drivers</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-emerald-400">{stats.totalUsers}</div>
          <div className="text-gray-400 text-sm">Total Users</div>
        </div>
      </div>

      {/* Daily Rides Chart */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Rides per Day (Last 7 Days)</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={dailyRides}>
            <XAxis dataKey="name" stroke="#9CA3AF" />
            <YAxis stroke="#9CA3AF" />
            <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: 'none' }} />
            <Bar dataKey="rides" fill="#10B981" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Revenue Trend */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Revenue Trend (Last 7 Days)</h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={revenueData}>
            <XAxis dataKey="name" stroke="#9CA3AF" />
            <YAxis stroke="#9CA3AF" />
            <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: 'none' }} />
            <Area type="monotone" dataKey="revenue" stroke="#10B981" fill="#10B981" fillOpacity={0.2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Payment Breakdown Pie Chart */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Payment Methods</h3>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie data={paymentBreakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
              {paymentBreakdown.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: 'none' }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
